import type { ApiErrorBody } from '@pl/domain';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
  }
}

export class NetworkError extends Error {
  constructor() {
    super('No connection to the server');
  }
}

type Method = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

/**
 * The session ended while the tab was open: an admin deactivated the person or reset their password,
 * or it simply expired. Whatever request finds that out first — a list refreshing, a save — tells
 * these listeners, so the tab goes to sign-in at once instead of failing quietly until `me` happens
 * to be asked again. `RequireAuth` listens.
 *
 * Three requests are left out, because their callers already answer a 401 in their own way:
 *  - sign-in answers a wrong password with 401, and that belongs on the sign-in page as it is;
 *  - `me` is the question the listeners ask in response, so announcing its own 401 would have them
 *    ask it again, forever;
 *  - a scan refused for a lost session shows the red "Signed out" screen, which says the box was
 *    not recorded and needs a tap before it goes to sign-in. Leaving at once would skip that.
 */
const sessionLost = new Set<() => void>();
const ANSWERED_BY_THE_CALLER = new Set(['/auth/login', '/auth/me', '/scans']);

/** Calls `fn` whenever a request is answered 401 for want of a session. Returns the unsubscribe. */
export function onSessionLost(fn: () => void): () => void {
  sessionLost.add(fn);
  return () => {
    sessionLost.delete(fn);
  };
}

function noticeSessionLost(path: string, res: Response): void {
  if (res.status !== 401 || ANSWERED_BY_THE_CALLER.has(path.split('?')[0]!)) return;
  for (const fn of [...sessionLost]) fn();
}

/**
 * The server refused a request because this person is still on a temporary password an admin gave
 * them (403 `PASSWORD_CHANGE_REQUIRED`). That is **not** a lost session: the session is fine, and is
 * exactly what the change screen needs to change the password. So the tab stays, and nothing about
 * who this is is forgotten; these listeners only ask `me` again, which then says `mustChangePassword`
 * and `RequireAuth` shows the change screen. `me` never answers this way itself.
 */
const passwordChangeRequired = new Set<() => void>();

/** Calls `fn` whenever a request is refused until this person chooses their own password. */
export function onPasswordChangeRequired(fn: () => void): () => void {
  passwordChangeRequired.add(fn);
  return () => {
    passwordChangeRequired.delete(fn);
  };
}

function noticePasswordChangeRequired(err: ApiError): void {
  if (err.status !== 403 || err.code !== 'PASSWORD_CHANGE_REQUIRED') return;
  for (const fn of [...passwordChangeRequired]) fn();
}

/**
 * Whether the app is on the scan surface, from where it is. The server gates what a phone may be told
 * on this (a count's result while another is open, a counted SKU's codes), so it is decided here,
 * once, for every request, and never per call.
 */
const onScanSurface = () => window.location.pathname === '/scan' || window.location.pathname.startsWith('/scan/');

async function send(method: Method, path: string, body?: unknown): Promise<Response> {
  const headers: Record<string, string> = {};
  if (body !== undefined) headers['content-type'] = 'application/json';
  if (method !== 'GET') headers['x-requested-with'] = 'pallet-level';
  if (onScanSurface()) headers['x-pl-surface'] = 'scan';
  try {
    return await fetch(`/api/v1${path}`, {
      method,
      credentials: 'same-origin',
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new NetworkError();
  }
}

async function toError(res: Response): Promise<ApiError> {
  const text = await res.text().catch(() => '');
  try {
    const parsed = JSON.parse(text) as ApiErrorBody;
    if (parsed?.error) return new ApiError(res.status, parsed.error.code, parsed.error.message, parsed.error.details);
  } catch {
    /* not JSON */
  }
  // No "Try again." here: every place this reaches the screen — RefetchNote, an ErrorNote beside
  // its own retry button — already sits right next to a Try again button of its own.
  return new ApiError(res.status, `HTTP_${res.status}`, res.status >= 500 ? 'The server had a problem.' : res.statusText || 'Request failed');
}

async function json<T>(method: Method, path: string, body?: unknown): Promise<T> {
  const res = await send(method, path, body);
  noticeSessionLost(path, res);
  if (!res.ok) {
    const err = await toError(res);
    noticePasswordChangeRequired(err);
    throw err;
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

export const api = {
  get: <T>(path: string) => json<T>('GET', path),
  post: <T>(path: string, body: unknown = {}) => json<T>('POST', path, body),
  patch: <T>(path: string, body: unknown = {}) => json<T>('PATCH', path, body),
  put: <T>(path: string, body: unknown = {}) => json<T>('PUT', path, body),
  delete: <T>(path: string) => json<T>('DELETE', path),
  async pdf(path: string, method: 'GET' | 'POST' = 'GET'): Promise<Blob> {
    const res = await send(method, path, method === 'POST' ? {} : undefined);
    noticeSessionLost(path, res);
    if (!res.ok) {
      const err = await toError(res);
      noticePasswordChangeRequired(err);
      throw err;
    }
    return res.blob();
  },
};
